import { useCallback, useRef, useState } from 'react';
import { gamingDayOffset } from '@dayon/contracts';
import { useErrorDialog, useToast } from '@dayon/design-system';
import { api, ApiRequestError } from '../../lib/api.ts';
import type { ExportOutcome } from './ExportDialog.tsx';

/**
 * Running the audit-trail export, and fetching the file it produced.
 *
 * A hook rather than a copy, because two screens offer this control and they
 * must produce the same act. Access control owns the trail; System
 * administration is where an operator goes when they need to hand the trail to
 * somebody, and its Export logs button used to be a dead affordance beside a
 * notice claiming no export route existed. Duplicating the ninety lines below
 * into the second screen would have given the platform two export paths to keep
 * in step, and the download is itself an audited act — two implementations
 * would eventually disagree about what gets written.
 *
 * Everything the operator decides is the window. The brand scope is the
 * requester's own and is never sent, so a file cannot carry a brand its
 * requester could not open.
 */
export interface AuditExport {
  open: boolean;
  from: string;
  to: string;
  errors: Record<string, string>;
  busy: boolean;
  outcome: ExportOutcome | null;
  /** Opens the dialog, clearing any outcome left from a previous run. */
  start: () => void;
  close: () => void;
  setFrom: (v: string) => void;
  setTo: (v: string) => void;
  submit: () => void;
  download: () => void;
}

export function useAuditExport(options: { onRan?: () => void } = {}): AuditExport {
  const { showError } = useErrorDialog();
  const { toast } = useToast();
  const onRan = options.onRan;

  /* Defaults to the last 30 days, which is the window somebody opening this
     control usually wants and is small enough to stay under the row cap on a
     busy platform — as Manila gaming days, which is what the export counts,
     and read again each time the dialog opens (`start`). */
  const [open, setOpen] = useState(false);
  const [from, setFrom] = useState(() => gamingDayOffset(-30));
  const [to, setTo] = useState(() => gamingDayOffset(0));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState<ExportOutcome | null>(null);

  /*
   * One download at a time, held in a REF rather than in state.
   *
   * Downloading an artefact appends a `report.download` row to the audit trail,
   * so an impatient double-click on a slow file writes the act twice and an
   * access review counts two exports where one person did one thing. A ref
   * because the guard has to be set and read within a single event, before any
   * re-render could deliver a state update.
   */
  const downloading = useRef(false);

  /* The dates are read again on every open, not kept from when the screen mounted: one loaded at
     23:50 Manila time and opened at 00:10 otherwise offered yesterday as the last day. */
  const start = useCallback(() => {
    setFrom(gamingDayOffset(-30));
    setTo(gamingDayOffset(0));
    setOutcome(null);
    setErrors({});
    setOpen(true);
  }, []);

  const close = useCallback(() => setOpen(false), []);

  const submit = useCallback(() => {
    const next: Record<string, string> = {};
    if (!from) next['from'] = 'Choose the first day to include.';
    if (!to) next['to'] = 'Choose the last day to include.';
    if (from && to && from > to) next['from'] = 'The range starts after it ends.';
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    setBusy(true);
    void (async () => {
      try {
        const result = await api.post<ExportOutcome>('/api/access/export', { from, to });
        setOutcome(result);
        // Not a toast: the outcome carries a row count and a truncation warning
        // the operator has to read before they act on the file.
        onRan?.();
      } catch (error: unknown) {
        if (error instanceof ApiRequestError && error.fields) setErrors(error.fields);
        showError({
          title: 'The audit log was not exported',
          message:
            error instanceof ApiRequestError
              ? error.message
              : 'Nothing was produced. Try again shortly.',
          ...(error instanceof ApiRequestError ? { code: error.code } : {}),
        });
      } finally {
        setBusy(false);
      }
    })();
  }, [from, to, onRan, showError]);

  /**
   * Fetch the stored artefact and hand it to the browser.
   *
   * A fetch rather than a navigation: the download route answers with the
   * envelope on a refusal, and navigating to it would replace the console with
   * raw JSON. This way a refusal reaches the error dialog like every other one.
   */
  const download = useCallback(() => {
    if (!outcome || downloading.current) return;
    downloading.current = true;
    void (async () => {
      try {
        const response = await fetch(outcome.downloadPath, { credentials: 'same-origin' });
        if (!response.ok) {
          const body = (await response.json()) as { error?: { code: string; message: string } };
          throw new ApiRequestError(response.status, {
            code: body.error?.code ?? 'INTERNAL',
            message: body.error?.message ?? 'The file could not be read.',
          });
        }
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = `audit-log-${outcome.from}-to-${outcome.to}-${outcome.ref}.csv`;
        document.body.append(anchor);
        anchor.click();
        anchor.remove();
        /* Revoked on the next task, not synchronously.
           Chromium starts reading the blob asynchronously after the click, so
           revoking in the same tick is a race against the download it just
           began — and a revoked object URL is a download that fails silently or
           is retried. */
        setTimeout(() => URL.revokeObjectURL(url), 0);
        toast(`${outcome.ref} downloaded. The download itself is recorded in the trail too.`);
      } catch (error: unknown) {
        showError({
          title: 'The file could not be downloaded',
          message:
            error instanceof ApiRequestError
              ? error.message
              : 'The export exists — try the reference from the Reports module.',
          ...(error instanceof ApiRequestError ? { code: error.code } : {}),
        });
      } finally {
        downloading.current = false;
      }
    })();
  }, [outcome, toast, showError]);

  return { open, from, to, errors, busy, outcome, start, close, setFrom, setTo, submit, download };
}
